/**
 * Authentication Context
 * Provides authentication state and functions throughout the app
 */
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import api from '../services/api';
import authService from '../services/authService';
import storageService from '../services/platform/storageService';

// Create Auth Context
const AuthContext = createContext();

/**
 * Auth Provider Component
 * Wraps the app and provides authentication functionality
 */
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const clearAuthStorage = useCallback(async () => {
    await storageService.removeItem('edsupp_token');
    await storageService.removeItem('accessCodeVerified');
    await storageService.removeItem('dashboardType');
    await storageService.removeItem('activeUserId');
    await storageService.removeItem('activeUserName');
    await storageService.removeItem('activeChildData');
    await storageService.removeItem('dashboardMode');
    delete axios.defaults.headers.common['Authorization'];
  }, []);

  // Initialize auth on mount
  useEffect(() => {
    const token = storageService.getItemSync('edsupp_token');
    const accessCodeVerified = storageService.getItemSync('accessCodeVerified');
    
    if (token) {
      // Set axios default header
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      
      // Verify token with backend
      api.get('/auth/me')
        .then(response => {
          const userData = response.data;
          
          // For coaching/school teachers and school admins, bypass access code requirement
          if (['coaching_teacher', 'school_teacher', 'school_admin'].includes(userData.role)) {
            setUser(userData);
          } 
          // For parent, child, super_admin, and qb_admin users, check if access code was verified
          else if (accessCodeVerified === 'true') {
            setUser(userData);
          } else {
            // Token exists but access code not verified - clear token
            clearAuthStorage();
          }
        })
        .catch((error) => {
          // Only clear the session on a genuine auth failure (401/403).
          // A timeout / network error / 5xx (e.g. Cloudflare 524 when the
          // origin is overloaded mid-generation) must NOT log the user out —
          // keep the token so they stay signed in and can retry.
          const status = error?.response?.status;
          if (status === 401 || status === 403) {
            clearAuthStorage();
          }
        })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, [clearAuthStorage]);

  // Listen for unauthorized events fired by the API interceptor
  useEffect(() => {
    const handleUnauthorized = () => {
      setUser(null);
    };
    window.addEventListener('auth:unauthorized', handleUnauthorized);
    return () => window.removeEventListener('auth:unauthorized', handleUnauthorized);
  }, []);

  /**
   * Login user
   * @param {string} email - User email
   * @param {string} password - User password
   * @returns {Object} Result with success status, user data, and token
   */
  const login = async (email, password) => {
    try {
      const response = await authService.login(email, password);
      const { access_token, user: userData } = response.data;
      
      await storageService.setItem('edsupp_token', access_token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      
      return { success: true, user: userData, token: access_token };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Login failed' 
      };
    }
  };

  const loginWithGoogle = async (googlePayload) => {
    try {
      const response = await authService.googleAuth(googlePayload);
      const { access_token, user: userData, is_new_user } = response.data;

      await storageService.setItem('edsupp_token', access_token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;

      return { success: true, user: userData, token: access_token, isNewUser: Boolean(is_new_user) };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || 'Google sign-in failed'
      };
    }
  };

  const loginWithApple = async (applePayload) => {
    try {
      const response = await authService.appleAuth(applePayload);
      const { access_token, user: userData, is_new_user } = response.data;

      await storageService.setItem('edsupp_token', access_token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;

      return { success: true, user: userData, token: access_token, isNewUser: Boolean(is_new_user) };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || 'Apple sign-in failed'
      };
    }
  };

  const loginWithMobileOtp = async (mobile, otp) => {
    try {
      const response = await authService.loginWithMobileOtp(mobile, otp);
      const { access_token, user: userData } = response.data;

      await storageService.setItem('edsupp_token', access_token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;

      return { success: true, user: userData, token: access_token };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || 'Mobile OTP login failed'
      };
    }
  };

  /**
   * Register new user
   * @param {Object} userData - User registration data
   * @returns {Object} Result with success status and user data
   */
  const register = async (userData) => {
    try {
      const response = await authService.register(userData);
      const { access_token, user: newUser } = response.data;
      
      await storageService.setItem('edsupp_token', access_token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      setUser(newUser);
      
      return { success: true, user: newUser };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Registration failed' 
      };
    }
  };

  /**
   * Logout user
   * Clears all authentication data and local storage
   */
  const logout = async () => {
    await clearAuthStorage();
    setUser(null);
  };

  const requestAccountDeletion = async (password, reason = '') => {
    try {
      const response = await authService.requestAccountDeletion({ password, reason });
      logout();
      return { success: true, data: response.data };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || 'Account deletion request failed'
      };
    }
  };

  const cancelAccountDeletion = async () => {
    try {
      const response = await authService.cancelAccountDeletion();
      return { success: true, data: response.data };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || 'Cancel account deletion failed'
      };
    }
  };

  /**
   * Verify access code
   * @param {string} code - Access code to verify
   * @returns {Object} Result with success status and user data
   */
  const verifyAccessCode = async (code) => {
    try {
      const response = await authService.verifyAccessCode(code);
      const userData = response.data;
      setUser(userData);
      return { success: true, user: userData };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || 'Invalid access code'
      };
    }
  };

  /**
   * Change user password
   * @param {string} currentPassword - Current password
   * @param {string} newPassword - New password
   * @returns {Object} Result with success status
   */
  const changePassword = async (currentPassword, newPassword) => {
    try {
      await authService.changePassword(currentPassword, newPassword);
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || 'Password change failed'
      };
    }
  };

  /**
   * Update user profile
   * @param {Object} profileData - Profile data to update
   * @returns {Object} Result with success status and updated user
   */
  const updateProfile = async (profileData) => {
    try {
      await authService.updateProfile(profileData);
      const meResponse = await api.get('/auth/me');
      const updatedUser = meResponse.data;
      setUser(updatedUser);
      return { success: true, user: updatedUser };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || 'Profile update failed'
      };
    }
  };

  /**
   * Update the current user.
   * Pass null to clear, a full user object to replace, or a partial object to merge.
   */
  const updateUser = (patch) => {
    if (patch === null) {
      setUser(null);
    } else {
      setUser((prev) => (prev ? { ...prev, ...patch } : patch));
    }
  };

  // Context value
  const value = {
    user,
    login,
    loginWithGoogle,
    loginWithApple,
    loginWithMobileOtp,
    register,
    logout,
    verifyAccessCode,
    changePassword,
    updateProfile,
    requestAccountDeletion,
    cancelAccountDeletion,
    loading,
    updateUser,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

/**
 * Custom hook to use Auth Context
 * @returns {Object} Auth context value
 * @throws {Error} If used outside AuthProvider
 */
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
